import React, { useEffect, useMemo, Suspense, lazy } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  AIReview,
  AIReviewRequest,
  EMPTY_PRESCRIPTION_GROUP,
  Patient,
  PERMISSION_PRESETS,
  PrescriptionGroup,
  PromptType,
  RequestPresets,
  TextConsultationContext,
  GUIDELINE_TO_DOCS_MAPPING,
} from "common/models/types";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  deletePatient,
  fetchPatientById,
  savePatient,
  savePrescription,
  uploadPrescriptionsByExcel,
  uploadPrescriptionsByImage,
} from "api/patientService";
import { useDispatch } from "react-redux";
import { setPatient, touchPatientUpdatedAt } from "store/actions";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import {
  fetchPatientPrescriptions,
  fetchPatientOTC,
  fetchPatientHFF,
  fetchPatientReview,
  fetchPatientReviews,
  fetchPatientList,
  fetchCheckReferenceBatch,
  fetchPatientConsultationRecords,
  fetchPatientDrugIdentifications,
} from "store/middleware";
import { AppDispatch } from "store";
import PatientDetailTemplate from "components/templates/PatientsTemplate/PatientDetailTemplate";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import ConsultationRecordDetail from "components/organisms/ConsultationRecordDetail";
import TextRecordSlide from "components/templates/TextRecordSlide";
import {
  fetchConsultationRecordById,
  createTextConsultationRecord,
  updateTextConsultationRecord,
  deleteConsultationRecord,
} from "api/consultationService";
import { fetchAIReviewById } from "api/aiReviewService";
import {
  flushAll as flushAllTextRecordQueues,
  isTextRecordOpen,
  textRecordEnvKey,
} from "components/templates/TextRecordSlide/textRecordSaveQueue";
import { createTextRecordSlideOpeners } from "./textRecordSlideOpeners";
import { kstToday } from "components/templates/TextRecordSlide/buildTextRecordBody";
import { DeleteTarget } from "components/organisms/PrescriptionHistoryViewer/MedicationTimelineView";
import PrescriptionFormTemplate from "components/templates/PrescriptionFormTemplate";
import AnalysisOptionsTemplate, {
  AnalysisOptions,
} from "components/templates/AnalysisOptionsTemplate";
import { searchDrug } from "api/drugService";
import { log, LogLevel } from "utils/LogUtil";
import { flattenPrescriptionGroup, groupPrescriptionsByHospital } from "utils/PrescriptionUtils";
import { isEncryptedExcel } from "utils/FileUtil";
import { exportMedicationHistoryToExcel } from "utils/exportMedicationHistory";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import OTCFormTemplate from "components/templates/OTCFormTemplate";
import HFFFormTemplate from "components/templates/HFFFormTemplate";
import { EMPTY_PRESCRIPTION, Prescription, PrescriptionHospitalGroup } from "common/models/types";
import { deletePrescription } from "api/patientService";
import {
  analyzePrescriptions,
  updateAIReview,
  ANALYSIS_REQUEST_FAILED_MESSAGE,
} from "api/aiReviewService";
import { deductPointsForAIAnalysis } from "api/pointsService";
import { eventLog } from "utils/AmplitudeUtil";
import { Schedule } from "common/models/types";
import { NhisTransactionStatus } from "types/nhis";
import { fetchNhisRequestsByPatient, cancelNhisRequest } from "api/nhisService";
import {
  fetchSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  CreateScheduleParams,
  UpdateScheduleParams,
} from "api/scheduleService";
import { Flex, COLOR_SYSTEM } from "components/atoms";
import { updateUserPoints } from "store/slices/userSlice";
import { MIN_APPROVAL_INFO_COUNT } from "common/constants";
import { getPremiumRole } from "common/constants/roles";

// 처방이력 뷰어는 ATC 분류표(~59KB) 등 무거운 의존성을 품어, 버튼으로 열 때만 로드(lazy)한다.
const PrescriptionHistoryViewerModal = lazy(
  () => import("components/organisms/PrescriptionHistoryViewer/PrescriptionHistoryViewerModal")
);

// 상담 기록 슬라이드 고유 key용 모듈 카운터(선례 openDoseReviewTestSlide).
let patientTextRecordOpenSeq = 0;

const PatientDetailPage: React.FC = () => {
  const { patientId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const patient = useSelector((state: RootState) =>
    patientId ? state.data.patients[parseInt(patientId)] : null
  );
  const referenceChecker = useSelector((state: RootState) => state.data.referenceChecker);
  const { isPremiumUser, isPaidPremiumUser, user } = useSelector((state: RootState) => state.user);

  // AI 검토 시작 이중 발사 방지: 동기 재진입 락 (doAnalysisStart · handleAnalysisOptionsSubmit 공유)
  const isAnalysisInFlightRef = React.useRef<boolean>(false);

  // 처방전 로딩 상태 추적
  const [prescriptionDataLoaded, setPrescriptionDataLoaded] = React.useState(false);
  
  // 병원별 그룹화된 처방전 상태
  const [hospitalGroupedPrescriptions, setHospitalGroupedPrescriptions] = React.useState<PrescriptionHospitalGroup[] | null>(null);

  // 처방이력 파악 뷰어 모달
  const [historyViewerOpen, setHistoryViewerOpen] = React.useState(false);

  // 저장 중복 클릭 방지
  const [isSaving, setIsSaving] = React.useState(false);

  // 방문 일정 상태
  const [schedules, setSchedules] = React.useState<Schedule[]>([]);

  const loadSchedules = React.useCallback(async () => {
    if (!patientId) return;
    const result = await fetchSchedules({ patientId: Number(patientId) });
    if (!result.error && result.data) {
      setSchedules(result.data);
    }
  }, [patientId]);

  React.useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  // 심평원 조회 요청 상태
  const [nhisRequests, setNhisRequests] = React.useState<NhisTransactionStatus[]>([]);

  const loadNhisRequests = React.useCallback(async () => {
    if (!patientId) return;
    const result = await fetchNhisRequestsByPatient(Number(patientId));
    if (!result.error && result.data) {
      setNhisRequests(result.data);
    }
  }, [patientId]);

  React.useEffect(() => {
    loadNhisRequests();
  }, [loadNhisRequests]);

  const handleCancelNhisRequest = React.useCallback(async (transactionId: string) => {
    ConfirmManager.show({
      title: "조회 요청 취소",
      body: (
        <div>
          <Text textType="Body1" style={{ marginBottom: "8px" }}>
            요청을 취소하시겠습니까?
          </Text>
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
            취소된 요청은 재사용할 수 없으며, 새 요청을 생성해야 합니다.
          </Text>
        </div>
      ),
      buttons: [
        <Button
          key="back"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => ConfirmManager.hide()}
        >
          돌아가기
        </Button>,
        <Button
          key="cancel-request"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            ConfirmManager.hide();
            const result = await cancelNhisRequest(transactionId);
            if (!result.error) {
              ToastManager.show({ content: "요청이 취소되었습니다.", duration: 3000 });
              loadNhisRequests();
            } else {
              ToastManager.show({ content: result.message || "취소에 실패했습니다.", duration: 3000 });
            }
          }}
        >
          요청 취소
        </Button>,
      ],
    });
  }, [loadNhisRequests]);

  // FCM nhis-status-update 이벤트 수신 시 갱신
  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.patientId && String(detail.patientId) === patientId) {
        loadNhisRequests();
      }
    };
    window.addEventListener("nhis-status-update", handler);
    return () => window.removeEventListener("nhis-status-update", handler);
  }, [patientId, loadNhisRequests]);

  // patientId가 변경되면 로딩 상태 리셋
  React.useEffect(() => {
    setPrescriptionDataLoaded(false);
    setHospitalGroupedPrescriptions(null); // 병원별 그룹 상태도 리셋
  }, [patientId]);
  const prescriptions = useMemo(() => patient?.prescriptions, [patient]);
  const otc = useMemo(() => patient?.otc, [patient]);
  const hff = useMemo(() => patient?.hff, [patient]);

  const existingHospitalNames = useMemo(() => {
    if (!patient?.prescriptionGroups) return [];
    const names = patient.prescriptionGroups
      .map((g) => g.hospital)
      .filter((name): name is string => !!name && name.trim() !== "");
    return Array.from(new Set(names));
  }, [patient?.prescriptionGroups]);

  // [2025-01-23] 추가: admin 권한 확인
  const isAdmin = user?.roles?.includes("admin") || false;

  // 처방전과 OTC 통계 계산 (중복 itemCode 제거)
  const prescriptionStats = useMemo(() => {
    if (!referenceChecker) {
      return {
        totalPrescriptions: 0,
        totalMedicines: 0,
        uniqueCount: 0,
        referenceCount: 0,
      };
    }

    // 처방전 그룹 개수 (patient.prescriptionGroups)
    const totalPrescriptions = patient?.prescriptionGroups?.length || 0;

    // 총 의약품 개수 (처방전 + OTC + 건기식) — 백엔드 분석이 hff도 포함하므로 함께 집계
    const totalMedicines =
      (prescriptions?.length || 0) + (otc?.length || 0) + (hff?.length || 0);

    // 중복되지 않는 itemCode만 추출 (처방전 + OTC + 건기식)
    const uniqueItemCodes = new Set<string>();

    // 처방전에서 itemCode 수집
    prescriptions?.forEach((prescription) => {
      if (prescription.itemCode) {
        uniqueItemCodes.add(prescription.itemCode);
      }
    });

    // OTC에서 itemCode 수집
    otc?.forEach((otcItem) => {
      if (otcItem.itemCode) {
        uniqueItemCodes.add(otcItem.itemCode);
      }
    });

    // 건기식(HFF)에서 itemCode 수집 — AI 분석 대상에 포함되므로 게이지/통계에도 반영
    hff?.forEach((hffItem) => {
      if (hffItem.itemCode) {
        uniqueItemCodes.add(hffItem.itemCode);
      }
    });

    // 각 고유한 itemCode의 referenceCount 합산
    let referenceCount = 0;
    uniqueItemCodes.forEach((itemCode) => {
      if (referenceChecker[itemCode]) {
        referenceCount += referenceChecker[itemCode].count || 0;
      }
    });

    return {
      totalPrescriptions,
      totalMedicines,
      uniqueCount: uniqueItemCodes.size,
      referenceCount,
    };
  }, [prescriptions, otc, hff, referenceChecker, patient]);

  const loadingProductLabel = useMemo(() => {
    if (!patient) return false;

    if (patient.prescriptions && patient.prescriptions.length > 0) {
      for (const prescription of patient.prescriptions) {
        if (
          prescription.itemCode &&
          (!referenceChecker?.[prescription.itemCode] ||
            referenceChecker?.[prescription.itemCode]?.count === -1)
        ) {
          return true;
        }
      }
    }

    if (patient.otc && patient.otc.length > 0) {
      for (const otc of patient.otc) {
        if (
          otc.itemCode &&
          (!referenceChecker?.[otc.itemCode] || referenceChecker?.[otc.itemCode]?.count === -1)
        ) {
          return true;
        }
      }
    }

    return false;
  }, [patient, referenceChecker]);

  // 처방전과 OTC 의약품의 참고 정보를 배치로 확인
  useEffect(() => {
    // prescriptions 또는 otc가 아직 로드되지 않았거나 비어있는 경우 체크
    if (!prescriptions && !otc && !hff) {
      return;
    }

    const itemCodes = [
      ...(prescriptions?.map((p) => p.itemCode).filter(Boolean) || []),
      ...(otc?.map((o) => o.itemCode).filter(Boolean) || []),
      ...(hff?.map((h) => h.itemCode).filter(Boolean) || []),
    ];

    // 아직 체크되지 않은 itemCode만 필터링
    // undefined: 아직 조회하지 않음 → 체크 필요
    // count === -1: 조회 중 → 스킵
    // count >= 0: 조회 완료 (0은 참고정보 없음) → 스킵
    const uncheckedItemCodes = itemCodes.filter((code) => {
      const checker = referenceChecker[code];
      return checker === undefined;
    });

    log(
      LogLevel.INFO,
      `[PatientDetailPage] 체크가 필요한 itemCodes: ${uncheckedItemCodes.length}개 / 전체: ${itemCodes.length}개`
    );

    if (uncheckedItemCodes.length > 0) {
      log(LogLevel.INFO, "[PatientDetailPage] fetchCheckReferenceBatch 호출!");
      dispatch(fetchCheckReferenceBatch(uncheckedItemCodes));
    }
  }, [prescriptions, otc, hff, referenceChecker, dispatch]);

  // 환자 정보(메모 등) 수정 - 자동저장용 (로딩 창 제거)
  const handleEditPatient = async (updatedPatient: Patient) => {
    try {
      // 자동저장에서는 로딩 창 제거
      // WaitingManager.show({ message: "환자 정보를 저장 중입니다..." });

      const response = await savePatient({
        ...updatedPatient,
        id: patient.id,
      });
      if (response.error) throw new Error(response.message || "환자 정보 저장 실패");
      dispatch(setPatient(response.data as Patient));
      eventLog("환자 정보 저장 성공", {
        patientId: patient.id,
      });
      ToastManager.show({ content: "✅ 환자 정보가 저장되었습니다.", duration: 2000 });
    } catch (error) {
      ToastManager.show({ content: "⛔️ 환자 정보 저장에 실패했습니다.", duration: 3000 });
      eventLog("환자 정보 저장 실패", {
        patientId: patient.id,
        error: error instanceof Error ? error.message : "알 수 없는 오류",
      });
    }
    // finally 블록도 제거 (WaitingManager.hide() 불필요)
  };

  // 질병 정보 수정
  const handleEditDiseases = async (diseases: string) => {
    if (!patient) return;
    const updatedPatient = { id: patient.id, diseases };
    await handleEditPatient(updatedPatient);
  };

  // 알레르기 정보 수정
  const handleEditAllergies = async (allergies: string) => {
    if (!patient) return;
    const updatedPatient = { id: patient.id, allergies };
    await handleEditPatient(updatedPatient);
  };

  // 추가 정보 수정
  const handleEditConsultationNotes = async (consultationNotes: string) => {
    if (!patient) return;
    const updatedPatient = { id: patient.id, consultationNotes };
    await handleEditPatient(updatedPatient);
  };

  // 메모 수정
  const handleEditMemo = async (memo: string) => {
    if (!patient) return;
    const updatedPatient = { id: patient.id, memo };
    await handleEditPatient(updatedPatient);
  };

  // 병원별/날짜별 보기 토글 핸들러
  const handleToggleViewMode = () => {
    if (!patient?.prescriptionGroups) return;
    
    if (hospitalGroupedPrescriptions) {
      // 날짜별 보기로 전환
      setHospitalGroupedPrescriptions(null);
    } else {
      // 병원별 보기로 전환
      const grouped = groupPrescriptionsByHospital(patient.prescriptionGroups);
      setHospitalGroupedPrescriptions(grouped);
    }
  };

  // 처방전 그룹 변경 시 병원별 그룹 재계산
  React.useEffect(() => {
    if (hospitalGroupedPrescriptions && patient?.prescriptionGroups) {
      // 병원별 보기 상태면 재그룹화
      const regrouped = groupPrescriptionsByHospital(patient.prescriptionGroups);
      setHospitalGroupedPrescriptions(regrouped);
    }
  }, [patient?.prescriptionGroups]); // prescriptionGroups 전체 변경 감지 (내용 변경 포함)

  // 처방전 추가/수정
  const handleEditPrescription = async (groupId?: string) => {
    try {
      const initialGroup = groupId
        ? patient.prescriptionGroups?.find((group) => group.groupId === groupId)
        : { ...EMPTY_PRESCRIPTION_GROUP, patientId: patient.id };

      // 파일 선택을 위한 input 엘리먼트 생성
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".xlsx, .xls"; // 엑셀 파일 확장자 필터
      fileInput.onchange = async (e) => {
        const target = e.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          const file = target.files[0];
          await handleExcelUpload(file);
        }
        fileInput.value = "";
      };

      // 이미지 파일 선택을 위한 input 엘리먼트 생성
      const imageFileInput = document.createElement("input");
      imageFileInput.type = "file";
      imageFileInput.accept = "image/*"; // 이미지 파일 확장자 필터
      imageFileInput.onchange = async (e) => {
        const target = e.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
          const file = target.files[0];
          await handleImageUpload(file);
        }
      };

      ConfirmManager.show({
        body: (
          <PrescriptionFormTemplate
            title={groupId ? "처방전 수정" : "새 처방전 추가"}
            initialGroup={initialGroup}
            existingHospitalNames={existingHospitalNames}
            onSubmit={handleSavePrescription}
            onCancel={() => {
              ConfirmManager.hide();
            }}
            onExcelUploadClick={() => fileInput.click()}
            onPictureUploadClick={() => imageFileInput.click()}
          />
        ),
        buttons: [],
        showBackdrop: true,
        backdropClose: false,
        showCloseButton: false,
      });
    } catch (error) {
      ToastManager.show({ content: "처방전 처리에 실패했습니다.", duration: 3000 });
    }
  };

  // 엑셀 파일 업로드 처리
  const handleExcelUpload = async (file: File) => {
    if (!patient || !patient.id) {
      ToastManager.show({ content: "환자 정보가 올바르지 않습니다.", duration: 3000 });
      return;
    }

    if (await isEncryptedExcel(file)) {
      ToastManager.show({
        content: (
          <>
            암호가 설정된 엑셀 파일은 업로드할 수 없습니다.
            <br />
            암호 해제: Excel &rarr; 파일 &rarr; 정보 &rarr; 통합 문서 보호 &rarr; 암호 설정 &rarr;
            빈칸으로 확인
          </>
        ),
        duration: 10000,
      });
      return;
    }

    try {
      WaitingManager.show({ message: "엑셀 파일을 업로드 중입니다..." });
      const response = await uploadPrescriptionsByExcel(patient.id, file);

      if (response.error) {
        throw new Error(response.message || "엑셀 파일 업로드에 실패했습니다.");
      }

      eventLog("환자처방전 저장", {
        patientId: patient.id,
        type: "excel",
      });

      const duplicateCount = response.data?.duplicateCount ?? 0;
      const toastMessage =
        duplicateCount > 0
          ? `엑셀 파일이 업로드되었습니다. (중복 ${duplicateCount}건 제외)`
          : "엑셀 파일이 성공적으로 업로드되었습니다.";
      ToastManager.show({ content: toastMessage, duration: 3000 });
      await dispatch(fetchPatientPrescriptions(patient.id)); // 처방전 목록 새로고침
      dispatch(touchPatientUpdatedAt(patient.id));
      ConfirmManager.hide(); // 현재 열려있는 Confirm 팝업 닫기
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "엑셀 파일 업로드 중 오류가 발생했습니다.";
      ToastManager.show({ content: errorMessage, duration: 3000 });
      eventLog("환자처방전 저장 실패", {
        patientId: patient.id,
        type: "excel",
        error: errorMessage,
      });
    } finally {
      WaitingManager.hide();
    }
  };

  // 이미지 파일 업로드 처리
  const handleImageUpload = async (file: File) => {
    if (!patient || !patient.id) {
      ToastManager.show({ content: "환자 정보가 올바르지 않습니다.", duration: 3000 });
      return;
    }

    try {
      WaitingManager.show({ message: "처방전 이미지를 업로드 중입니다..." });
      const response = await uploadPrescriptionsByImage(patient.id, file);

      if (response.error) {
        throw new Error(response.message || "이미지 파일 업로드에 실패했습니다.");
      }

      ToastManager.show({
        content: "처방전 이미지가 성공적으로 업로드되었습니다.",
        duration: 2000,
      });

      await dispatch(fetchPatientPrescriptions(patient.id)); // 처방전 목록 새로고침
      dispatch(touchPatientUpdatedAt(patient.id));

      eventLog("환자처방전 저장", {
        patientId: patient.id,
        type: "image",
      });

      ConfirmManager.hide(); // 현재 열려있는 Confirm 팝업 닫기
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "이미지 파일 업로드 중 오류가 발생했습니다.";
      ToastManager.show({ content: errorMessage, duration: 3000 });
      eventLog("환자처방전 저장 실패", {
        patientId: patient.id,
        type: "image",
        error: errorMessage,
      });
    } finally {
      WaitingManager.hide();
    }
  };

  // 처방전 삭제
  const handleDeletePrescription = async (groupId: string) => {
    try {
      const targetGroup = patient.prescriptionGroups?.find((group) => group.groupId === groupId);
      if (!targetGroup) {
        throw new Error("삭제할 처방전을 찾을 수 없습니다.");
      }

      ConfirmManager.show({
        title: "처방전 삭제",
        body: (
          <div>
            <Text textType="Body1" style={{ marginBottom: "16px" }}>
              {targetGroup.hospital}의 처방전을 삭제하시겠습니까?
            </Text>
            <Text textType="Body2" color="var(--gray-600)">
              삭제된 처방전은 복구할 수 없습니다.
            </Text>
          </div>
        ),
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={() => {
              ConfirmManager.hide();
            }}
          >
            취소
          </Button>,
          <Button
            key="delete"
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={async () => {
              try {
                WaitingManager.show({ message: "처방전을 삭제 중입니다..." });

                // 처방전 그룹에 속한 모든 처방전 삭제 (병렬 처리)
                const prescriptionsToDelete = flattenPrescriptionGroup(targetGroup);
                log(LogLevel.INFO, `처방전 ${prescriptionsToDelete.length}개 삭제 시작`);

                // Promise.all로 모든 삭제 시도 (병렬 처리)
                // deletePrescription은 에러를 throw하지 않고 { error: true } 형태로 반환
                const deleteResults = await Promise.all(
                  prescriptionsToDelete.map((prescription) =>
                    deletePrescription(prescription.id)
                  )
                );

                // 성공/실패 집계
                const successCount = deleteResults.filter((r) => !r.error).length;
                const failCount = deleteResults.filter((r) => r.error).length;

                log(LogLevel.INFO, "삭제 결과:", {
                  total: prescriptionsToDelete.length,
                  success: successCount,
                  failed: failCount,
                });

                // 결과에 따른 피드백
                if (failCount > 0) {
                  if (successCount > 0) {
                    // 부분 성공
                    ToastManager.show({
                      content: `${successCount}개 삭제 성공, ${failCount}개 실패했습니다.`,
                      duration: 4000,
                    });
                    eventLog("환자처방전 부분 삭제", {
                      patientId: patient.id,
                      groupId: groupId,
                      successCount,
                      failCount,
                    });
                  } else {
                    // 전체 실패
                    throw new Error("처방전 삭제에 실패했습니다.");
                  }
                } else {
                  // 전체 성공
                  ToastManager.show({ content: "처방전이 삭제되었습니다.", duration: 2000 });
                  eventLog("환자처방전 삭제", {
                    patientId: patient.id,
                    groupId: groupId,
                    prescriptionCount: prescriptionsToDelete.length,
                  });
                }

                ConfirmManager.hide();
              } catch (error) {
                eventLog("환자처방전 삭제 실패", {
                  patientId: patient.id,
                  groupId: groupId,
                  error: error instanceof Error ? error.message : "알 수 없는 오류",
                });
                ToastManager.show({ content: "처방전 삭제에 실패했습니다.", duration: 3000 });
                ConfirmManager.hide();
              } finally {
                // 성공/실패 여부와 관계없이 최신 데이터로 동기화
                await dispatch(fetchPatientPrescriptions(patient.id));
                dispatch(touchPatientUpdatedAt(patient.id));
                WaitingManager.hide();
              }
            }}
          >
            삭제
          </Button>,
        ],
      });
    } catch (error) {
      ToastManager.show({ content: "처방전 삭제에 실패했습니다.", duration: 3000 });
    }
  };

  // 처방전 저장 처리
  const handleSavePrescription = async (group: PrescriptionGroup) => {
    if (isSaving) {
      log(LogLevel.WARNING, "이미 저장 중입니다. 중복 클릭 무시.");
      return;
    }

    try {
      setIsSaving(true);
      WaitingManager.show({ message: "처방전을 저장 중입니다..." });
      log(LogLevel.VERBOSE, "저장할 처방전 그룹 데이터:", JSON.stringify(group, null, 2));

      // 처방약이 없으면 저장하지 않음
      if (group.prescriptions.length === 0) {
        throw new Error("저장할 처방 약품이 없습니다.");
      }

      // 모든 처방약에 환자 ID와 처방일 설정
      const prescriptionsToSave = flattenPrescriptionGroup(group).map(p => ({
        ...p,
        source: p.source || (p.medicationName ? "manual" : undefined),
      }));

      log(LogLevel.VERBOSE, "저장할 처방약 목록:", JSON.stringify(prescriptionsToSave, null, 2));

      // 각 저장 요청의 결과와 원본 prescription을 함께 추적
      const savePromises = prescriptionsToSave.map(async (prescription, index) => {
        log(LogLevel.VERBOSE, "저장 요청 처방약:", JSON.stringify(prescription, null, 2));
        const result = await savePrescription(prescription);
        return { result, index, prescription };
      });

      // 삭제 처리
      const deletePromises = group.deletedPrescriptionIds.map(async (prescriptionId) => {
        log(LogLevel.VERBOSE, "삭제 요청 처방약 ID:", prescriptionId);
        const result = await deletePrescription(prescriptionId);
        return { result, prescriptionId };
      });

      const saveResults = await Promise.all(savePromises);
      const deleteResults = await Promise.all(deletePromises);

      let hasError = false;
      const errorMessages: string[] = [];

      // 저장 결과 처리 및 성공한 항목의 ID 반영
      saveResults.forEach(({ result, index, prescription }) => {
        if (result.error) {
          hasError = true;
          errorMessages.push(`${prescription.medicationName || '처방약'}: ${result.message}`);
          log(LogLevel.ERROR, `처방약 저장 실패 (index: ${index}):`, result.message);
        } else if (result.data) {
          const savedPrescription = Array.isArray(result.data) ? result.data[0] : result.data;

          // ✅ group.prescriptions[index] 객체를 직접 수정
          // 이 객체는 PrescriptionFormTemplate의 state와 같은 참조이므로
          // 여기서 id를 설정하면 원본 state도 업데이트됨
          group.prescriptions[index].id = savedPrescription.id;

          log(LogLevel.INFO, `처방약 저장 성공 (index: ${index}, id: ${savedPrescription.id})`);
        }
      });

      // 삭제 결과 처리
      deleteResults.forEach(({ result, prescriptionId }) => {
        if (result.error) {
          hasError = true;
          errorMessages.push(`삭제 실패 (ID: ${prescriptionId}): ${result.message}`);
          log(LogLevel.ERROR, `처방약 삭제 실패 (id: ${prescriptionId}):`, result.message);
        } else {
          log(LogLevel.INFO, `처방약 삭제 성공 (id: ${prescriptionId})`);
        }
      });

      if (hasError) {
        // ✅ 팝업 유지, state 업데이트 불필요
        // group.prescriptions 객체들에는 이미 id가 설정되어 있음
        eventLog("환자처방전 부분 저장 실패", {
          patientId: patient.id,
          groupId: group.groupId,
          errorCount: errorMessages.length,
          errors: errorMessages,
        });

        ToastManager.show({
          content: `일부 처방전 저장에 실패했습니다:\n${errorMessages.join('\n')}`,
          duration: 5000,
        });
        return; // 사용자가 실패한 항목 수정 후 다시 저장 가능
      }

      // 모두 성공한 경우
      eventLog("환자처방전 저장", {
        patientId: patient.id,
        groupId: group.groupId,
      });

      ToastManager.show({ content: "처방전이 저장되었습니다.", duration: 2000 });
      ConfirmManager.hide();
    } catch (error) {
      eventLog("환자처방전 저장 실패", {
        patientId: patient.id,
        groupId: group.groupId,
        error: error instanceof Error ? error.message : "알 수 없는 오류",
      });
      ToastManager.show({ content: "처방전 저장에 실패했습니다.", duration: 3000 });
      ConfirmManager.hide();
    } finally {
      if (patient?.id) {
        await dispatch(fetchPatientPrescriptions(patient.id));
        dispatch(touchPatientUpdatedAt(patient.id));
      }
      WaitingManager.hide();
      setIsSaving(false);
    }
  };

  // 일반의약품 처방전 수정
  const handleEditOtc = async (prescriptionId?: number) => {
    if (!patient) return;
    try {
      const initialPrescription = prescriptionId
        ? patient.otc?.find((p) => p.id === prescriptionId)
        : { ...EMPTY_PRESCRIPTION, patientId: patient.id, type: "otc" };

      ConfirmManager.show({
        body: (
          <OTCFormTemplate
            title={prescriptionId ? "일반의약품 수정" : "새 일반의약품 추가"}
            initialPrescription={initialPrescription}
            patientId={patient.id}
            onSubmit={handleSaveOtc}
            onCancel={() => {
              ConfirmManager.hide();
            }}
          />
        ),
        showBackdrop: true,
        backdropClose: false,
        showCloseButton: false,
      });
    } catch (error) {
      ToastManager.show({ content: "일반의약품 정보 처리에 실패했습니다.", duration: 3000 });
    }
  };

  // 일반의약품 처방전 삭제
  const handleDeleteOtc = async (prescriptionId: number) => {
    if (!patient) return;
    try {
      const targetOtc = patient.otc?.find((p) => p.id === prescriptionId);
      if (!targetOtc) {
        throw new Error("삭제할 일반의약품 정보를 찾을 수 없습니다.");
      }

      ConfirmManager.show({
        title: "일반의약품 삭제",
        body: (
          <div>
            <Text textType="Body1" style={{ marginBottom: "16px" }}>
              {`'${targetOtc.medicationName || "알 수 없음"}' 일반의약품 정보를 삭제하시겠습니까?`}
            </Text>
            <Text textType="Body2" color="var(--gray-600)">
              삭제된 정보는 복구할 수 없습니다.
            </Text>
          </div>
        ),
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={() => {
              ConfirmManager.hide();
            }}
          >
            취소
          </Button>,
          <Button
            key="delete"
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={async () => {
              try {
                WaitingManager.show({ message: "일반의약품 정보를 삭제 중입니다..." });
                const result = await deletePrescription(prescriptionId);
                if (result.error) {
                  throw new Error(result.message || "일반의약품 삭제에 실패했습니다.");
                }
                ToastManager.show({ content: "일반의약품 정보가 삭제되었습니다.", duration: 2000 });
                await dispatch(fetchPatientOTC(patient.id));
                dispatch(touchPatientUpdatedAt(patient.id));
                ConfirmManager.hide();
                eventLog("환자일반의약품 삭제", {
                  patientId: patient.id,
                  prescriptionId: prescriptionId,
                });
              } catch (error) {
                ToastManager.show({
                  content: "일반의약품 정보 삭제에 실패했습니다.",
                  duration: 3000,
                });
                eventLog("환자일반의약품 삭제 실패", {
                  patientId: patient.id,
                  prescriptionId: prescriptionId,
                  error: error instanceof Error ? error.message : "알 수 없는 오류",
                });
              } finally {
                WaitingManager.hide();
              }
            }}
          >
            삭제
          </Button>,
        ],
      });
    } catch (error) {
      ToastManager.show({ content: "일반의약품 정보 삭제 처리에 실패했습니다.", duration: 3000 });
    }
  };

  const handleSaveOtc = async (prescription: Prescription) => {
    if (!patient) return;
    try {
      WaitingManager.show({ message: "일반의약품 정보를 저장 중입니다..." });
      const result = await savePrescription({
        ...prescription,
        source: prescription.source || (prescription.medicationName ? "manual" : undefined),
      });

      if (result.error) {
        throw new Error(result.message || "일반의약품 정보 저장에 실패했습니다.");
      }
      ToastManager.show({ content: "일반의약품 정보가 저장되었습니다.", duration: 2000 });
      await dispatch(fetchPatientOTC(patient.id));
      dispatch(touchPatientUpdatedAt(patient.id));
      eventLog("환자일반의약품 저장", {
        patientId: patient.id,
        prescriptionId: prescription.id || "new",
      });
      ConfirmManager.hide(); // 폼 닫기
    } catch (error) {
      eventLog("환자일반의약품 저장 실패", {
        patientId: patient.id,
        prescriptionId: prescription.id || "new",
        error: error instanceof Error ? error.message : "알 수 없는 오류",
      });
      ToastManager.show({ content: "일반의약품 정보 저장에 실패했습니다.", duration: 3000 });
    } finally {
      WaitingManager.hide();
    }
  };

  // 건강기능식품 처방전 수정
  const handleEditHff = async (prescriptionId?: number) => {
    if (!patient) return;
    try {
      const initialPrescription = prescriptionId
        ? patient.hff?.find((p) => p.id === prescriptionId)
        : { ...EMPTY_PRESCRIPTION, patientId: patient.id, type: "hff" };

      ConfirmManager.show({
        body: (
          <HFFFormTemplate
            title={prescriptionId ? "건강기능식품 수정" : "새 건강기능식품 추가"}
            initialPrescription={initialPrescription}
            patientId={patient.id}
            onSubmit={handleSaveHff}
            onCancel={() => {
              ConfirmManager.hide();
            }}
          />
        ),
        buttons: [],
        showBackdrop: true,
        backdropClose: false,
        showCloseButton: false,
      });
    } catch (error) {
      ToastManager.show({ content: "건강기능식품 정보 처리에 실패했습니다.", duration: 3000 });
    }
  };

  // 건강기능식품 처방전 삭제
  const handleDeleteHff = async (prescriptionId: number) => {
    if (!patient) return;
    try {
      const targetHff = patient.hff?.find((p) => p.id === prescriptionId);
      if (!targetHff) {
        throw new Error("삭제할 건강기능식품 정보를 찾을 수 없습니다.");
      }

      ConfirmManager.show({
        title: "건강기능식품 삭제",
        body: (
          <div>
            <Text textType="Body1" style={{ marginBottom: "16px" }}>
              {`'${
                targetHff.medicationName || "알 수 없음"
              }' 건강기능식품 정보를 삭제하시겠습니까?`}
            </Text>
            <Text textType="Body2" color="var(--gray-600)">
              삭제된 정보는 복구할 수 없습니다.
            </Text>
          </div>
        ),
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={() => {
              ConfirmManager.hide();
            }}
          >
            취소
          </Button>,
          <Button
            key="delete"
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={async () => {
              try {
                WaitingManager.show({ message: "건강기능식품 정보를 삭제 중입니다..." });
                const result = await deletePrescription(prescriptionId);
                if (result.error) {
                  throw new Error(result.message || "건강기능식품 삭제에 실패했습니다.");
                }
                ToastManager.show({
                  content: "건강기능식품 정보가 삭제되었습니다.",
                  duration: 2000,
                });
                await dispatch(fetchPatientHFF(patient.id));
                dispatch(touchPatientUpdatedAt(patient.id));
                ConfirmManager.hide();
                eventLog("환자건강기능식품 삭제", {
                  patientId: patient.id,
                  prescriptionId: prescriptionId,
                });
              } catch (error) {
                ToastManager.show({
                  content: "건강기능식품 정보 삭제에 실패했습니다.",
                  duration: 3000,
                });
                eventLog("환자건강기능식품 삭제 실패", {
                  patientId: patient.id,
                  prescriptionId: prescriptionId,
                  error: error instanceof Error ? error.message : "알 수 없는 오류",
                });
              } finally {
                WaitingManager.hide();
              }
            }}
          >
            삭제
          </Button>,
        ],
      });
    } catch (error) {
      ToastManager.show({ content: "건강기능식품 정보 삭제 처리에 실패했습니다.", duration: 3000 });
    }
  };

  const handleSaveHff = async (prescription: Prescription) => {
    if (!patient) return;
    try {
      WaitingManager.show({ message: "건강기능식품 정보를 저장 중입니다..." });
      const result = await savePrescription({
        ...prescription,
        source: prescription.source || (prescription.medicationName ? "manual" : undefined),
      });

      if (result.error) {
        throw new Error(result.message || "건강기능식품 정보 저장에 실패했습니다.");
      }
      ToastManager.show({ content: "건강기능식품 정보가 저장되었습니다.", duration: 2000 });
      await dispatch(fetchPatientHFF(patient.id));
      dispatch(touchPatientUpdatedAt(patient.id));
      ConfirmManager.hide(); // 폼 닫기
      eventLog("환자건강기능식품 저장", {
        patientId: patient.id,
        prescriptionId: prescription.id || "new",
      });
    } catch (error) {
      eventLog("환자건강기능식품 저장 실패", {
        patientId: patient.id,
        prescriptionId: prescription.id || "new",
        error: error instanceof Error ? error.message : "알 수 없는 오류",
      });
      ToastManager.show({ content: "건강기능식품 정보 저장에 실패했습니다.", duration: 3000 });
    } finally {
      WaitingManager.hide();
    }
  };

  const handleDeletePatient = async () => {
    if (!patient || !patient.id) {
      ToastManager.show({ content: "환자 정보가 올바르지 않습니다." });
      return;
    }
    ConfirmManager.show({
      title: "환자 삭제",
      body: "환자 정보를 삭제하시겠습니까?",
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => {
            ConfirmManager.hide();
          }}
        >
          취소
        </Button>,
        <Button
          key="delete"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={doDeletePatient}
        >
          삭제
        </Button>,
      ],
      showBackdrop: true,
      backdropClose: true,
      showCloseButton: false,
    });
  };

  const doDeletePatient = async () => {
    try {
      WaitingManager.show({ message: "환자 정보를 삭제 중입니다..." });
      ConfirmManager.hide();
      const result = await deletePatient(patient.id);
      if (result.error) {
        throw new Error(result.message || "환자 정보 삭제에 실패했습니다.");
      }
      ToastManager.show({ content: "환자 정보가 삭제되었습니다.", duration: 2000 });
      dispatch(fetchPatientList({ forceRefresh: true }));
      navigate("/dashboard");
      eventLog("환자 삭제", {
        patientId: patient.id,
      });
    } catch (error) {
      ToastManager.show({ content: "환자 정보 삭제에 실패했습니다.", duration: 3000 });
      eventLog("환자 삭제 실패", {
        patientId: patient.id,
        error: error instanceof Error ? error.message : "알 수 없는 오류",
      });
    } finally {
      WaitingManager.hide();
    }
  };

  // --- 방문 일정 CRUD 핸들러 ---
  const handleCreateSchedule = async (data: CreateScheduleParams) => {
    try {
      const result = await createSchedule(Number(patientId), data);
      if (result.error) throw new Error(result.message);
      ToastManager.show({ content: "일정이 추가되었습니다.", duration: 2000 });
      eventLog("방문일정_추가", { patientId: Number(patientId), scheduledAt: data.scheduledAt });
      await loadSchedules();
    } catch (error) {
      ToastManager.show({ content: "일정 추가에 실패했습니다.", duration: 3000 });
    }
  };

  const handleUpdateSchedule = async (scheduleId: number, data: UpdateScheduleParams) => {
    try {
      const result = await updateSchedule(scheduleId, data);
      if (result.error) throw new Error(result.message);
      ToastManager.show({ content: "일정이 수정되었습니다.", duration: 2000 });
      eventLog("방문일정_수정", { patientId: Number(patientId), scheduleId });
      await loadSchedules();
    } catch (error) {
      ToastManager.show({ content: "일정 수정에 실패했습니다.", duration: 3000 });
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    ConfirmManager.show({
      title: "일정 삭제",
      body: "이 일정을 삭제하시겠습니까?",
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="delete"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            try {
              const result = await deleteSchedule(scheduleId);
              if (result.error) throw new Error(result.message);
              ToastManager.show({ content: "일정이 삭제되었습니다.", duration: 2000 });
              eventLog("방문일정_삭제", { patientId: Number(patientId), scheduleId });
              await loadSchedules();
              ConfirmManager.hide();
            } catch (error) {
              ToastManager.show({ content: "일정 삭제에 실패했습니다.", duration: 3000 });
            }
          }}
        >
          삭제
        </Button>,
      ],
      showBackdrop: true,
      backdropClose: false,
      showCloseButton: false,
    });
  };

  const handleAnalysisStart = async () => {
    // 포인트 확인 및 프롬프트 선택을 위한 ConfirmManager 표시
    if (!patient || !patient.id) {
      ToastManager.show({ content: "환자 정보가 올바르지 않습니다.", duration: 3000 });
      return;
    }

    if (patient.prescriptions?.length === 0 && patient.otc?.length === 0) {
      ToastManager.show({ content: "분석할 처방 정보가 없습니다.", duration: 3000 });
      return;
    }

    const currentPoints = user?.userPoints?.currentPoints || 0;
    const requiredPoints = 200;
    const availablePromptTypes = user?.prompts || [PromptType.MEDICATION_COUNSELING];

    // AnalysisPaymentTemplate를 사용하여 포인트 확인 및 프롬프트 선택
    const AnalysisPaymentTemplateModule = await import(
      "../../components/templates/AnalysisPaymentTemplate"
    );
    const AnalysisPaymentTemplate = AnalysisPaymentTemplateModule.default;

    ConfirmManager.show({
      title: "AI 분석을 시작하시겠습니까?",
      body: (
        <AnalysisPaymentTemplate
          onSubmit={(selectedPromptType) => {
            ConfirmManager.hide();
            checkReferenceAndAnalyze(selectedPromptType);
          }}
          currentPoints={currentPoints}
          requiredPoints={requiredPoints}
          availablePromptTypes={availablePromptTypes}
        />
      ),
      showCloseButton: true,
      backdropClose: true,
    });
  };

  const checkReferenceAndAnalyze = async (promptType: PromptType) => {
    if (!patient || !patient.id) return;

    // referenceChecker 확인
    let noItemCodeList: Prescription[] = [];
    let notEnoughReferenceList: Prescription[] = [];
    let crawlingList: Prescription[] = [];

    if (patient.prescriptions.length > 0) {
      patient.prescriptions.forEach((prescription) => {
        if (!prescription.itemCode) {
          noItemCodeList.push(prescription);
        } else if (!referenceChecker?.[prescription.itemCode]) {
          crawlingList.push(prescription);
        } else if (
          prescription.itemCode &&
          referenceChecker?.[prescription.itemCode]?.count === -1
        ) {
          crawlingList.push(prescription);
        } else if (
          referenceChecker?.[prescription.itemCode]?.count >= 0 &&
          referenceChecker?.[prescription.itemCode]?.count < MIN_APPROVAL_INFO_COUNT
        ) {
          notEnoughReferenceList.push(prescription);
        }
      });
    }

    if (patient.otc.length > 0) {
      patient.otc.forEach((otc) => {
        if (!otc.itemCode) {
          noItemCodeList.push(otc);
        } else if (!referenceChecker?.[otc.itemCode]) {
          crawlingList.push(otc);
        } else if (otc.itemCode && referenceChecker?.[otc.itemCode]?.count === -1) {
          crawlingList.push(otc);
        } else if (
          referenceChecker?.[otc.itemCode]?.count >= 0 &&
          referenceChecker?.[otc.itemCode]?.count < MIN_APPROVAL_INFO_COUNT
        ) {
          notEnoughReferenceList.push(otc);
        }
      });
    }

    if (noItemCodeList.length > 0 || notEnoughReferenceList.length > 0 || crawlingList.length > 0) {
      ConfirmManager.show({
        title: "의약품 정보 확인 필요",
        body: (
          <Flex direction="column" gap="16px">
            {noItemCodeList.length > 0 && (
              <Flex direction="column" gap="8px">
                <Text textType="Body1" color={COLOR_SYSTEM.get("Red")[600]}>
                  📌 품목기준코드가 없는 의약품이 있습니다
                </Text>
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                  {noItemCodeList.map((p) => p.medicationName).join(", ")}
                </Text>
                <Flex direction="column" gap="4px" style={{ backgroundColor: COLOR_SYSTEM.get("Gray")[50], padding: "12px", borderRadius: "8px" }}>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                    ✅ 필요한 조치:
                  </Text>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                    • 처방전 이력에서 수정버튼을 눌러 정확한 의약품을 입력해주세요
                  </Text>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                    • 의약품명을 입력하고 엔터를 눌러 검색한 의약품을 입력해야합니다
                  </Text>
                </Flex>
              </Flex>
            )}
            
            {notEnoughReferenceList.length > 0 && (
              <Flex direction="column" gap="8px">
                <Text textType="Body1" color={COLOR_SYSTEM.get("Orange")[600]}>
                  ⚠️ 허가사항 정보가 없는 의약품이 있습니다
                </Text>
                <Flex direction="column" gap="4px" style={{ maxHeight: "150px", overflowY: "auto" }}>
                  {notEnoughReferenceList.map((p) => (
                    <Flex key={p.id} direction="row" gap="8px" align="center" style={{ paddingLeft: "8px" }}>
                      <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>
                        • {p.medicationName}
                      </Text>
                      {p.itemCode && (
                        <a
                          href={`https://nedrug.mfds.go.kr/pbp/CCBBB01/getItemDetail?itemSeq=${p.itemCode}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: COLOR_SYSTEM.get("Blue")[600],
                            fontSize: "12px",
                            textDecoration: "underline",
                            whiteSpace: "nowrap"
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          허가사항 확인하기
                        </a>
                      )}
                    </Flex>
                  ))}
                </Flex>
                <Flex direction="column" gap="4px" style={{ backgroundColor: COLOR_SYSTEM.get("Gray")[50], padding: "12px", borderRadius: "8px" }}>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                    ✅ 필요한 조치:
                  </Text>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                    • 허가사항 확인하기를 눌러 상태를 확인하세요
                  </Text>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                    • 취소/취하된 경우: 같은 성분의 다른 제품으로 변경 필요
                  </Text>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                    • 정상 제품이나 허가사항의 내용이 적은 경우: '현재 정보로 분석하기'로 진행(예, 멸균증류수)
                  </Text>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[600]}>
                    • 허가사항이 있는데 오류인 경우: 문의하기를 통해 알려주세요
                  </Text>
                </Flex>
              </Flex>
            )}
            
            <Text textType="Body2" color={COLOR_SYSTEM.get("Blue")[600]} style={{ marginTop: "8px" }}>
              💡 지금 분석을 진행하시면 현재 확보된 정보만으로 분석됩니다.
            </Text>
          </Flex>
        ),
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={() => {
              ConfirmManager.hide();
            }}
          >
            뒤로가기
          </Button>,
          <Button
            key="confirm"
            buttonType="button"
            color="Primary"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={() => {
              ConfirmManager.hide();
              doAnalysisStart(promptType);
            }}
          >
            현재 정보로 분석하기
          </Button>,
        ],
      });
    } else {
      doAnalysisStart(promptType);
    }
  };

  const doAnalysisStart = async (selectedPromptType?: PromptType) => {
    // 동기 재진입 락: await 이전에 잡아 이중 발사 차단
    if (isAnalysisInFlightRef.current) return;
    isAnalysisInFlightRef.current = true;

    try {
      // 포인트 확인 (Premium 사용자는 제외)
      const currentPoints = user?.userPoints?.currentPoints || 0;
      const requiredPoints = 200;

      if (!isPremiumUser && currentPoints < requiredPoints) {
        ConfirmManager.show({
          title: "포인트가 부족합니다",
          body: (
            <Flex direction="column" gap="12px">
              <Text textType="Body1">AI 분석에는 {requiredPoints}P가 필요합니다.</Text>
              <Text textType="Body1">현재 보유 포인트: {currentPoints.toLocaleString()}P</Text>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                포인트를 충전하시겠습니까?
              </Text>
            </Flex>
          ),
          buttons: [
            <Button
              key="cancel"
              buttonType="button"
              color="Tertiary"
              size="Medium"
              type="Text"
              variant="Outlined"
              onClick={() => {
                ConfirmManager.hide();
              }}
            >
              취소
            </Button>,
            <Button
              key="charge"
              buttonType="button"
              color="Primary"
              size="Medium"
              type="Text"
              variant="Contained"
              onClick={() => {
                ConfirmManager.hide();
                navigate("/mypage/billing?tab=pricing");
              }}
            >
              포인트 충전하기
            </Button>,
          ],
        });
        return;
      }

      WaitingManager.show({ message: "처방전 분석을 요청 중입니다..." });

      // 선택된 프롬프트 타입 사용 (기본값: MEDICATION_COUNSELING)
      const finalPromptType = selectedPromptType || PromptType.MEDICATION_COUNSELING;

      // RequestPresets에 정의되지 않은 프롬프트 타입의 경우 기본값 사용
      const requestPreset = RequestPresets[finalPromptType] || {
        promptType: finalPromptType,
        docsOptions: ["SPSTv3"],
        medicationOptions: PERMISSION_PRESETS.OPTION2,
      };

      const response = await analyzePrescriptions({
        ...requestPreset,
        patientId: patient.id,
        admin: {
          // targetProcessingStep: AIReviewStatus.PROCESSING_PROMPT_CREATION,
        },
        initialAnalysis: {
          prescriptionCount: prescriptionStats.totalPrescriptions,
          totalMedicines: prescriptionStats.totalMedicines,
          uniqueMedicines: prescriptionStats.uniqueCount,
          referenceCount: prescriptionStats.referenceCount,
        },
      }, isPremiumUser);

      if (response.error || !response.data?.reviewId) {
        // analyzePrescriptions는 실패 시 항상 고정 한글 문구를 채운다(서버 봉투 오류만 서버 문구 그대로).
        throw new Error(response.message || ANALYSIS_REQUEST_FAILED_MESSAGE);
      }

      ToastManager.show({ content: "분석 요청이 성공적으로 완료되었습니다.", duration: 2000 });

      // 포인트 차감 (Premium 사용자는 제외)
      if (user?.id && !isPremiumUser) {
        try {
          await deductPointsForAIAnalysis({
            userId: user.id,
            reviewId: response.data.reviewId,
            points: 200,
            description: `AI 처방전 분석 - 환자 #${patient.id} - 리뷰 #${response.data.reviewId}`,
          });

          // 포인트 정보 업데이트
          dispatch(updateUserPoints());
        } catch (pointError) {
          log(LogLevel.ERROR, "포인트 차감 실패:", pointError);
          // 포인트 차감 실패해도 분석은 이미 진행되었으므로 계속 진행
          ToastManager.show({
            content: "포인트 차감에 실패했습니다. 고객센터에 문의해주세요.",
            duration: 3000,
          });
        }
      } else if (isPremiumUser && !isPaidPremiumUser) {
        // 면허번호/약사회 등 무료 Premium 사용자: premiumRole 업데이트
        // 유료 프리미엄(isPaidPremiumUser)는 백엔드가 자동으로 "유료프리미엄"으로 기록하므로 호출 skip
        const premiumRole = getPremiumRole(user?.roles);
        if (premiumRole) {
          try {
            await updateAIReview(response.data.reviewId, { premiumRole });
            log(LogLevel.INFO, `Premium Role 업데이트 완료: ${premiumRole}`);
          } catch (roleError) {
            log(LogLevel.ERROR, "Premium Role 업데이트 실패:", roleError);
            // Premium Role 업데이트 실패는 분석에 영향을 주지 않으므로 경고만 로깅
          }
        }
      }

      dispatch(fetchPatientReviews(patient.id) as any);
      dispatch(fetchPatientReview(response.data.reviewId) as any);
      dispatch(touchPatientUpdatedAt(patient.id));
      // 분석 결과 페이지로 이동
      navigate(`/patients/${patient.id}/reviews/${response.data.reviewId}`);
      eventLog("환자처방전 분석 요청", {
        patientId: patient.id,
        reviewId: response.data.reviewId,
      });
    } catch (error) {
      log(LogLevel.ERROR, "처방전 분석 요청 실패:", error);
      // 위에서 던진 Error의 문구가 곧 화면 문구다 — 폴백 문구를 여러 벌 두지 않는다.
      const errorMessage =
        error instanceof Error && error.message ? error.message : ANALYSIS_REQUEST_FAILED_MESSAGE;
      ToastManager.show({ content: errorMessage, duration: 3000 });
      eventLog("환자처방전 분석 요청 실패", {
        patientId: patient.id,
        error: errorMessage,
      });
    } finally {
      isAnalysisInFlightRef.current = false;
      WaitingManager.hide();
    }
  };

  // [2025-06-05] 추가: Admin 전용 보고서 생성 핸들러
  const handleAdminAnalysisStart = async () => {
    if (!patient || !patient.id) {
      ToastManager.show({ content: "환자 정보가 올바르지 않습니다.", duration: 3000 });
      return;
    }

    if (!isAdmin) {
      ToastManager.show({ content: "관리자 권한이 필요합니다.", duration: 3000 });
      return;
    }

    // [2025-01-23] 수정: 분석옵션설정 팝업 띄우기
    ConfirmManager.show({
      title: "관리자 전용 분석 옵션 설정",
      body: (
        <AnalysisOptionsTemplate
          onSubmit={handleAnalysisOptionsSubmit}
          onCancel={() => {
            ConfirmManager.hide();
          }}
        />
      ),
      buttons: [],
      showBackdrop: true,
      backdropClose: true,
      showCloseButton: false,
    });
  };

  // [2025-01-23] 추가: 분석옵션 제출 핸들러
  const handleAnalysisOptionsSubmit = async (options: AnalysisOptions) => {
    log(LogLevel.INFO, "PatientDetailPage:handleAnalysisOptionsSubmit", options);
    if (!patient || !patient.id) {
      ToastManager.show({ content: "환자 정보가 올바르지 않습니다.", duration: 3000 });
      return;
    }

    // 동기 재진입 락: doAnalysisStart와 공유 (사용자/admin 흐름 동시 발사 차단)
    if (isAnalysisInFlightRef.current) return;
    isAnalysisInFlightRef.current = true;

    try {
      WaitingManager.show({ message: "관리자 전용 처방전 분석을 요청 중입니다..." });

      // 가이드라인 타입을 docsOptions 형식으로 변환
      const docsOptions = options.guidelineTypes
        .map((guidelineType) => GUIDELINE_TO_DOCS_MAPPING[guidelineType])
        .filter((doc): doc is string => doc !== undefined);

      // [2025-01-17] 추가: 허가사항 옵션 처리
      const medicationOptions = options.permissionsEnabled ? options.selectedPermissions : [];

      // [2025-01-23] 디버깅: 전달되는 데이터 확인
      const requestData: AIReviewRequest = {
        patientId: patient.id,
        docsOptions: docsOptions,
        medicationOptions: medicationOptions, // 허가사항 옵션 추가
        initialAnalysis: {
          prescriptionCount: prescriptionStats.totalPrescriptions,
          totalMedicines: prescriptionStats.totalMedicines,
          uniqueMedicines: prescriptionStats.uniqueCount,
          referenceCount: prescriptionStats.referenceCount,
        },
        // [2025-01-23] 수정: admin 전용 분석도 완전한 분석을 진행하도록 targetProcessingStep 제거
        // admin: {
        //   targetProcessingStep: AIReviewStatus.PROCESSING_PROMPT_CREATION,
        // },
      };

      // promptId가 있으면 promptId와 promptType을 모두 사용, 없으면 promptType만 사용
      if (options.promptId) {
        requestData.promptId = options.promptId;
        requestData.promptType = options.promptType; // promptId를 사용해도 promptType은 필요
      } else {
        requestData.promptType = options.promptType;
      }

      // [2025-01-27] 추가: selectedModel이 있으면 모델 지정
      if (options.selectedModel) {
        requestData.modelType = options.selectedModel;
      }

      log(LogLevel.INFO, "분석 요청 데이터:", JSON.stringify(requestData, null, 2));

      const response = await analyzePrescriptions(requestData, isPremiumUser);

      if (response.error || !response.data?.reviewId) {
        throw new Error(response.message || ANALYSIS_REQUEST_FAILED_MESSAGE);
      }

      ToastManager.show({
        content: "관리자 전용 분석 요청이 성공적으로 완료되었습니다.",
        duration: 2000,
      });

      // 면허번호/약사회 등 무료 Premium 사용자만 premiumRole 업데이트
      // 유료 프리미엄은 백엔드 자동 처리
      if (isPremiumUser && !isPaidPremiumUser) {
        const premiumRole = getPremiumRole(user?.roles);
        if (premiumRole) {
          try {
            await updateAIReview(response.data.reviewId, { premiumRole });
            log(LogLevel.INFO, `Admin 분석 - Premium Role 업데이트 완료: ${premiumRole}`);
          } catch (roleError) {
            log(LogLevel.ERROR, "Admin 분석 - Premium Role 업데이트 실패:", roleError);
            // Premium Role 업데이트 실패는 분석에 영향을 주지 않으므로 경고만 로깅
          }
        }
      }

      dispatch(fetchPatientReviews(patient.id) as any);
      dispatch(fetchPatientReview(response.data.reviewId) as any);
      dispatch(touchPatientUpdatedAt(patient.id));
      ConfirmManager.hide(); // 팝업 닫기
      // 분석 결과 페이지로 이동
      navigate(`/patients/${patient.id}/reviews/${response.data.reviewId}`);
      eventLog("관리자 전용 처방전 분석 요청", {
        patientId: patient.id,
        reviewId: response.data.reviewId,
      });
    } catch (error) {
      log(LogLevel.ERROR, "관리자 전용 처방전 분석 요청 실패:", error);
      const errorMessage =
        error instanceof Error && error.message ? error.message : ANALYSIS_REQUEST_FAILED_MESSAGE;
      ToastManager.show({ content: errorMessage, duration: 3000 });
      eventLog("관리자 전용 처방전 분석 요청 실패", {
        patientId: patient.id,
        error: errorMessage,
      });
    } finally {
      isAnalysisInFlightRef.current = false;
      WaitingManager.hide();
    }
  };

  useEffect(() => {
    const loadPatientData = async () => {
      if (!patientId) return;

      try {
        WaitingManager.show({
          message: "환자 정보를 불러오는 중입니다...",
        });

        const response = await fetchPatientById(parseInt(patientId));

        if (response.error) {
          throw new Error(response.message || "환자 정보를 불러오는데 실패했습니다.");
        }

        dispatch(setPatient(response.data as Patient));
      } catch (error) {
        log(LogLevel.ERROR, "환자 정보 로드 오류:", error);
        ToastManager.show({
          content: "환자 정보를 불러오는데 실패했습니다.",
          duration: 3000,
        });
      } finally {
        WaitingManager.hide();
      }
    };

    if (patientId && !patient) {
      loadPatientData();
    }
  }, [patientId, patient, dispatch]);

  // 처방전 정보 로드
  useEffect(() => {
    const loadPrescriptionData = async () => {
      if (!patientId || !patient || prescriptionDataLoaded) return;

      setPrescriptionDataLoaded(true); // 로딩 시작 표시

      // 처방전 정보가 없는 경우에만 로드
      if (!patient.prescriptions) {
        try {
          WaitingManager.show({
            message: "처방전 정보를 불러오는 중입니다...",
          });

          await dispatch(fetchPatientPrescriptions(parseInt(patientId))).unwrap();
        } catch (error) {
          log(LogLevel.ERROR, "처방전 정보 로드 오류:", error);
          ToastManager.show({
            content: "처방전 정보를 불러오는데 실패했습니다.",
            duration: 3000,
          });
        } finally {
          WaitingManager.hide();
        }
      }

      // 일반의약품 처방전 로드
      if (!patient.otc) {
        try {
          WaitingManager.show({
            message: "일반의약품 처방전 정보를 불러오는 중입니다...",
          });

          await dispatch(fetchPatientOTC(parseInt(patientId))).unwrap();
        } catch (error) {
          log(LogLevel.ERROR, "일반의약품 처방전 정보 로드 오류:", error);
          ToastManager.show({
            content: "일반의약품 처방전 정보를 불러오는데 실패했습니다.",
            duration: 3000,
          });
        } finally {
          WaitingManager.hide();
        }
      }

      // 건강기능식품 처방전 로드
      if (!patient.hff) {
        try {
          WaitingManager.show({
            message: "건강기능식품 처방전 정보를 불러오는 중입니다...",
          });

          await dispatch(fetchPatientHFF(parseInt(patientId))).unwrap();
        } catch (error) {
          log(LogLevel.ERROR, "건강기능식품 처방전 정보 로드 오류:", error);
          ToastManager.show({
            content: "건강기능식품 처방전 정보를 불러오는데 실패했습니다.",
            duration: 3000,
          });
        } finally {
          WaitingManager.hide();
        }
      }
    };

    loadPrescriptionData();
  }, [patientId, patient, dispatch, prescriptionDataLoaded]);

  // AI 검토 이력 로드
  useEffect(() => {
    if (!patientId || !patient || patient.aiReviews) return;
    dispatch(fetchPatientReviews(parseInt(patientId)) as any).catch((error: any) => {
      log(LogLevel.ERROR, "AI 검토 이력 로드 오류:", error);
    });
  }, [patientId, patient, dispatch]);

  // 상담 기록 로드
  useEffect(() => {
    if (!patientId || !patient || patient.consultationRecords) return;
    dispatch(fetchPatientConsultationRecords(parseInt(patientId))).unwrap().catch((error) => {
      log(LogLevel.ERROR, "상담 기록 로드 오류:", error);
    });
  }, [patientId, patient, dispatch]);

  // 식별 의약품 로드
  useEffect(() => {
    if (!patientId || !patient || patient.drugIdentifications) return;
    dispatch(fetchPatientDrugIdentifications(parseInt(patientId))).unwrap().catch((error) => {
      log(LogLevel.ERROR, "식별 의약품 로드 오류:", error);
    });
  }, [patientId, patient, dispatch]);

  // 식별 의약품 삭제 핸들러
  const handleDeleteDrugIdentification = React.useCallback((id: number) => {
    if (!patientId) return;
    dispatch(fetchPatientDrugIdentifications(parseInt(patientId)));
    dispatch(touchPatientUpdatedAt(parseInt(patientId)));
  }, [patientId, dispatch]);

  // ── 상담 체크리스트(텍스트 기록) — 「AI 검토 이력」 보고서 줄 칸 + 읽기·수정·삭제 (설계 결정 7·13·16·20) ──────────
  const myUserId = user?.id;
  const isOwnPatient = !!patient && !!myUserId && patient.userId === myUserId;

  // 첫 생성·삭제·상담일 변경·닫힘 때만 목록 재조회 + touch 로컬 반영(설계 결정 16 — PATCH마다 하지 않는다).
  // 보고서 줄 칸("작성됨"/「작성」)도 이 목록(reviewId·source)으로 판정하므로 함께 갱신된다.
  const afterTextRecordSave = () => {
    if (!patient?.id) return;
    dispatch(fetchPatientConsultationRecords(patient.id));
    dispatch(touchPatientUpdatedAt(patient.id));
  };

  // 보고서 상세 로더(본문 error !== 0이면 실패, fetchAIReviewById는 throw하므로 try/catch).
  // deletedAt이 있으면(관리자 계정은 삭제된 보고서도 200으로 받는다) null — 수정 띠 판정에서 제외(설계 결정 19).
  const loadReviewDetail = async (rid: number): Promise<AIReview | null> => {
    try {
      const res = await fetchAIReviewById(rid);
      if (!res || res.error !== 0 || !res.data) return null;
      if (res.data.deletedAt) return null;
      return res.data;
    } catch {
      return null;
    }
  };

  // 삭제 확인 → soft delete(기존 래퍼). 진행 중엔 재진입 막음은 ConfirmManager가 대신.
  function confirmDeleteRecord(recordId: number) {
    ConfirmManager.show({
      title: "",
      body: "이 체크리스트를 삭제할까요?",
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="delete"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            ConfirmManager.hide();
            const res = await deleteConsultationRecord(recordId);
            if (res.error === 0) {
              SlideManager.hide();
              afterTextRecordSave();
            } else if (res.status === 404) {
              // 이미 지워진 기록(다른 탭) — 문구 후 닫기 + 목록 재조회(줄 칸 복귀, 결함 4).
              ToastManager.show({ content: "기록을 찾을 수 없어요.", duration: 3000 });
              SlideManager.hide();
              afterTextRecordSave();
            } else {
              ToastManager.show({ content: "기록을 삭제하지 못했어요.", duration: 3000 });
            }
          }}
        >
          삭제
        </Button>,
      ],
      showBackdrop: true,
      backdropClose: false,
      showCloseButton: false,
    });
  }

  // 읽기·수정 슬라이드 열기 — 둘 다 진행 중인 저장 큐가 빈 뒤에 연다(설계 결정 21 결함 ④).
  const { showReadSlide, showEditSlide } = createTextRecordSlideOpeners({
    patientId: patient?.id ?? 0,
    patient: patient ?? null,
    onCreate: createTextConsultationRecord,
    onUpdate: updateTextConsultationRecord,
    afterSave: afterTextRecordSave,
    onDelete: confirmDeleteRecord,
    reopenFromServer: (recordId) => void openTextRecord(recordId),
  });

  // "작성됨" — 기존 기록 열기. 복약지도한 날(consultedAt)이 오늘(KST)이면 바로 수정, 아니면 읽기(결정 20).
  async function openTextRecord(recordId: number) {
    if (!patient?.id) return;
    // 진행 중인 저장(직전 슬라이드의 대기 변경)이 끝난 뒤 상세를 조회한다(옛 내용 경쟁 차단).
    await flushAllTextRecordQueues();
    const detail = await fetchConsultationRecordById(recordId);
    if (detail.error !== 0 || !detail.data?.context) {
      ToastManager.show({
        content: detail.status === 404 ? "기록을 찾을 수 없어요." : "기록을 불러오지 못했어요.",
        duration: 3000,
      });
      return;
    }
    const context = detail.data.context as unknown as TextConsultationContext;
    const isToday = context.consultedAt === kstToday();
    // 같은 환자·보고서의 수정 화면이 이미 열려 있으면 다시 열지 않는다(열림 가드 — 보고서 화면과 같음).
    if (isToday && isTextRecordOpen(textRecordEnvKey(patient.id, context.reviewId ?? null, "edit"))) return;
    let review: AIReview | null = null;
    if (context.reviewId != null) review = await loadReviewDetail(context.reviewId);
    if (isToday) await showEditSlide(recordId, context, review, false);
    else await showReadSlide(recordId, context, review);
  }

  // 「작성」 — 그 보고서를 고정한 채 새 기록으로 연다(보고서 선택 없음 — 결정 20, 설계 결정 17 폐기).
  async function openNewTextRecord(reviewId: number) {
    if (!patient?.id) return;
    const pid = patient.id;
    if (isTextRecordOpen(textRecordEnvKey(pid, reviewId, "edit"))) return; // 같은 열림이면 무시
    await flushAllTextRecordQueues();
    const review = await loadReviewDetail(reviewId);
    const key = `text-record:new:${reviewId}:${++patientTextRecordOpenSeq}`;
    SlideManager.show({
      body: (
        <TextRecordSlide
          key={key}
          slideKey={key}
          patientId={pid}
          reviewId={reviewId}
          recordId={null}
          review={review}
          initialContext={null}
          mode="edit"
          patient={patient}
          onCreate={createTextConsultationRecord}
          onUpdate={updateTextConsultationRecord}
          onCreated={() => {
            ToastManager.show({ content: "체크리스트를 저장했어요.", duration: 2500 });
            afterTextRecordSave();
          }}
          onConsultedAtChanged={afterTextRecordSave}
          onClose={afterTextRecordSave}
        />
      ),
      width: "560px",
      noBackdrop: true,
      needCloseButton: false,
    });
  }

  // 「상담 녹음 기록」 구역 줄 클릭 — 녹음 상세(텍스트 기록은 보고서 줄 칸으로만 연다 — 결정 20).
  const handleSelectConsultationRecord = (recordId: number) => {
    SlideManager.show({
      body: <ConsultationRecordDetail recordId={recordId} />,
      direction: "right",
      width: "520px",
      needCloseButton: true,
    });
  };

  // 처방이력 뷰어: 약(itemCode) 단위로 해당 약의 모든 처방 삭제
  const handleDeleteMedicines = React.useCallback((target: DeleteTarget) => {
    if (!patient?.id || target.prescriptionIds.length === 0) return;

    ConfirmManager.show({
      title: "약품 삭제",
      body: (
        <div style={{ width: 360, maxWidth: "80vw" }}>
          <Text textType="Body1" style={{ display: "block", marginBottom: "12px" }}>
            이 약을 분석에서 제외할까요?
          </Text>
          <div
            style={{
              padding: "10px 12px",
              marginBottom: "12px",
              background: COLOR_SYSTEM.get("Gray")[100],
              border: `1px solid ${COLOR_SYSTEM.get("Gray")[200]}`,
              borderRadius: "8px",
              fontWeight: 700,
              wordBreak: "break-all",
            }}
          >
            {target.medicationName}
          </div>
          <Text textType="Body1" style={{ display: "block" }}>
            병원과 관계없이 모든 처방에서 삭제되며, <strong>삭제 후에는 복구할 수 없습니다.</strong>
          </Text>
        </div>
      ),
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Tertiary"
          size="Medium"
          type="Text"
          variant="Outlined"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="delete"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            try {
              WaitingManager.show({ message: "약품을 삭제 중입니다..." });
              const results = await Promise.all(
                target.prescriptionIds.map((id) => deletePrescription(id))
              );
              const successCount = results.filter((r) => !r.error).length;
              const failCount = results.filter((r) => r.error).length;

              if (failCount > 0 && successCount === 0) {
                throw new Error("약품 삭제에 실패했습니다.");
              }

              ToastManager.show({
                content:
                  failCount > 0
                    ? `${successCount}개 삭제 성공, ${failCount}개 실패했습니다.`
                    : `${target.medicationName}이(가) 삭제되었습니다.`,
                duration: 2000,
              });
              eventLog("처방이력뷰어 약품삭제", {
                patientId: patient.id,
                itemCode: target.itemCode,
                count: target.prescriptionIds.length,
              });
              ConfirmManager.hide();
            } catch (error) {
              ToastManager.show({ content: "약품 삭제에 실패했습니다.", duration: 3000 });
              ConfirmManager.hide();
            } finally {
              await dispatch(fetchPatientPrescriptions(patient.id));
              dispatch(touchPatientUpdatedAt(patient.id));
              WaitingManager.hide();
            }
          }}
        >
          삭제
        </Button>,
      ],
    });
  }, [patient, dispatch]);

  if (!patient) {
    return (
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 32 }}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 12,
            boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.03)",
            marginBottom: 24,
            padding: 24,
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: 600, color: "#222", marginBottom: 12 }}>
            환자 정보를 불러오는 중입니다...
          </h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <PatientDetailTemplate
        patient={patient}
        referenceChecker={referenceChecker}
        hospitalGroupedPrescriptions={hospitalGroupedPrescriptions}
        onToggleViewMode={handleToggleViewMode}
        onEditPatient={(updated) => {
          handleEditPatient(updated);
        }}
        onEditDiseases={(diseases) => {
          handleEditDiseases(diseases);
        }}
        onEditAllergies={(allergies) => {
          handleEditAllergies(allergies);
        }}
        onEditConsultationNotes={(consultationNotes) => {
          handleEditConsultationNotes(consultationNotes);
        }}
        onEditMemo={(memo) => {
          handleEditMemo(memo);
        }}
        onEditPrescription={handleEditPrescription}
        onDeletePrescription={handleDeletePrescription}
        onEditOtc={handleEditOtc}
        onDeleteOtc={handleDeleteOtc}
        onEditHff={handleEditHff}
        onDeleteHff={handleDeleteHff}
        onAnalysisStart={handleAnalysisStart}
        onAdminAnalysisStart={isAdmin ? handleAdminAnalysisStart : undefined}
        onDeletePatient={handleDeletePatient}
        onExportMedicationHistory={() => exportMedicationHistoryToExcel({ patient })}
        onOpenHistoryViewer={() => setHistoryViewerOpen(true)}
        loadingProductLabel={loadingProductLabel}
        isPremiumUser={isPremiumUser}
        prescriptionStats={prescriptionStats}
        schedules={schedules}
        onCreateSchedule={handleCreateSchedule}
        onUpdateSchedule={handleUpdateSchedule}
        onDeleteSchedule={handleDeleteSchedule}
        onSelectConsultationRecord={handleSelectConsultationRecord}
        currentUserId={myUserId}
        onWriteChecklist={isOwnPatient ? (rid) => void openNewTextRecord(rid) : undefined}
        onOpenChecklist={isOwnPatient ? (recordId) => void openTextRecord(recordId) : undefined}
        onDeleteDrugIdentification={handleDeleteDrugIdentification}
        nhisRequests={nhisRequests}
        onCancelNhisRequest={handleCancelNhisRequest}
      />
      {historyViewerOpen && (
        <Suspense fallback={null}>
          <PrescriptionHistoryViewerModal
            isOpen={historyViewerOpen}
            onClose={() => setHistoryViewerOpen(false)}
            prescriptions={patient.prescriptions ?? []}
            patientId={patient.id}
            onDeleteMedication={handleDeleteMedicines}
          />
        </Suspense>
      )}
    </>
  );
};

export default PatientDetailPage;
